import { trackLLMGeneration } from './analyticsService';
import { MealFrequencyPreferences } from '../redux/userPreferencesSlice';
import { getAuthHeaders } from './apiClient';
import { getApiBaseUrl } from '../config/apiBase';

export interface ParsedDietaryPreferences {
  ingredientExclusions: string[];
  mealFrequency: MealFrequencyPreferences;
}

const PARSE_PROMPT = (text: string) => `Extract dietary preferences from the following text.

Return ONLY a JSON object. No markdown, no explanation, just JSON.

Structure:
{
  "ingredientExclusions": [],
  "mealFrequency": {
    "vegetarianPerWeek": null,
    "fishPerWeek": null
  }
}

ingredientExclusions: lowercase ingredient names the user dislikes or is allergic to.
vegetarianPerWeek: integer if user mentions how many vegetarian/meatless meals per week, null otherwise.
fishPerWeek: integer if user mentions how many fish/seafood meals per week, null otherwise.

Example input: "I'm allergic to eggplant and want 2 vegetarian meals per week"
Example output: {"ingredientExclusions":["eggplant"],"mealFrequency":{"vegetarianPerWeek":2,"fishPerWeek":null}}

Example input: "no tofu, I'd like fish twice a week"
Example output: {"ingredientExclusions":["tofu"],"mealFrequency":{"vegetarianPerWeek":null,"fishPerWeek":2}}

Example input: "one meatless dinner weekly"
Example output: {"ingredientExclusions":[],"mealFrequency":{"vegetarianPerWeek":1,"fishPerWeek":null}}

Example input: "no shellfish please"
Example output: {"ingredientExclusions":["shellfish"],"mealFrequency":{"vegetarianPerWeek":null,"fishPerWeek":null}}

Text: "${text}"`;

export async function parseDietaryPreferences(freeText: string): Promise<ParsedDietaryPreferences> {
  const empty: ParsedDietaryPreferences = { ingredientExclusions: [], mealFrequency: {} };
  if (!freeText.trim()) return empty;

  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getApiBaseUrl()}/api/ai/diet-parser`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ prompt: PARSE_PROMPT(freeText) }),
    });

    if (!response.ok) return empty;

    const data = await response.json();
    const responseText = data.result;

    trackLLMGeneration({
      $ai_trace_id: Math.random().toString(36).slice(2) + Date.now().toString(36),
      $ai_span_name: 'diet_preference_parser',
      $ai_model: data.model,
      $ai_provider: 'google',
      $ai_input_tokens: data.input_tokens,
      $ai_output_tokens: data.output_tokens,
      $ai_latency: data.latency_s,
    });

    let json = responseText;
    const codeBlockMatch = json.match(/```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/);
    if (codeBlockMatch) {
      json = codeBlockMatch[1].trim();
    }

    const parsed = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return empty;

    const ingredientExclusions: string[] = Array.isArray(parsed.ingredientExclusions)
      ? parsed.ingredientExclusions
          .filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
          .map((item: string) => item.toLowerCase().trim())
      : [];

    const freq = parsed.mealFrequency ?? {};
    const mealFrequency: MealFrequencyPreferences = {};
    if (typeof freq.vegetarianPerWeek === 'number' && freq.vegetarianPerWeek > 0) {
      mealFrequency.vegetarianPerWeek = Math.round(freq.vegetarianPerWeek);
    }
    if (typeof freq.fishPerWeek === 'number' && freq.fishPerWeek > 0) {
      mealFrequency.fishPerWeek = Math.round(freq.fishPerWeek);
    }

    return { ingredientExclusions, mealFrequency };
  } catch {
    return empty;
  }
}
